import { isNative } from './platform';
// ── "Novedades de MiPAE" — novedades one-shot por versión de catálogo ─────────
// Responde el feedback real de colegas ("muchos cambios y no se entiende",
// 31-jul): cada actualización GRANDE se explica UNA vez, en una card sobria
// con Benito como narrador — jamás un modal, jamás a mitad de turno.
// Única excepción, decidida por Sebastián el 20-oct-2026 entre tres maquetas:
// la invitación del 25 (`lo_nuevo_25`) sube como hoja UNA vez al abrir la app
// (membresias/AvisoLoNuevo.jsx), siempre cerrable y nunca a mitad de turno.
//
// Catálogo cerrado con `orden` numérico propio: APP_VERSION es un hash de git
// (sirve de identidad, no es ordenable), así que el orden lo lleva el
// catálogo. Publicar una novedad = agregar la entrada con el orden siguiente.
// La IDENTIDAD del conjunto es ese orden: corregir el texto de una entrada ya
// reconocida no la vuelve a mostrar (auditoría editorial 2026-09-28, E10).
//
// Cada entrada lleva `tipo`, catálogo cerrado:
//   herramienta  algo nuevo que se puede usar (la card lo anuncia como tal)
//   mejora       algo que ya existía y cambió
//   aviso        el aviso dentro de la app que la Política de Privacidad o
//                los Términos prometen ante un cambio material. Cerrar la
//                card NO equivale a consentir nada: solo reconoce que se leyó.
//
// Persistencia (2026-09-28): el reconocimiento vive en la CUENTA
// (`profiles.novedades_visto`, lib/novedadesCuenta.js) con localStorage como
// espejo síncrono. Este módulo es PURO respecto de la red: solo conoce el
// espejo local y el catálogo, para poder fijarse por test sin montar nada.
// Fail-safe hacia NO molestar (sin storage → no se muestra nada), mismo
// contrato que coachVisto.

import { accesoPlusAbierto, ESTUDIANTE_FIN_TRANSICION } from './planGating';

export const TIPOS_NOVEDAD = Object.freeze(['herramienta', 'mejora', 'aviso']);

export const NOVEDADES = [

  {
    orden: 1,
    id: 'apertura_directa',
    tipo: 'mejora',
    titulo: 'La app abre donde estás trabajando',
    detalle: 'Con un turno en curso, entrar a MiPAE te deja con el paciente que estás atendiendo, con el micrófono listo. Si lo prefieres, se apaga en tu perfil.',
  },
  {
    orden: 2,
    id: 'visita_escribe',
    tipo: 'mejora',
    titulo: 'La visita escribe tu evolución',
    detalle: 'Lo que marcas en la visita de enfermería y en la técnica queda escrito en el sistema que corresponde de tu evolución. Se pregunta una sola vez.',
  },
  {
    orden: 3,
    id: 'examen_control',
    tipo: 'herramienta',
    titulo: 'Examen de control durante el turno',
    detalle: 'Puedes repetir el examen físico partiendo del que hiciste al recibir y corregir solo lo que cambió. La evolución cuenta los cambios entre las dos fotos.',
  },
  {
    orden: 4,
    id: 'trayectoria',
    tipo: 'mejora',
    titulo: 'La trayectoria del turno',
    // Describe lo que la trayectoria hace de verdad (lib/trayectoria.js):
    // "Se recibió" es hecho, "Se decidió" es PLAN y "Actualmente" es hecho.
    // Un plan jamás se escribe como hecho, por eso dice "qué se decidió" y
    // no "qué se hizo".
    detalle: 'La evolución reúne cómo estaba el paciente al recibirlo, qué se decidió y cómo se encuentra al cierre, según lo registrado.',
  },
  {
    orden: 5,
    id: 'home_momento',
    tipo: 'mejora',
    titulo: 'El inicio se adapta a tu turno',
    detalle: 'Saliendo del turno ves tu cierre arriba. En pleno turno, la vuelta a tu paciente. Y Benito te saluda según el momento real, con atajos listos para tocar.',
  },
  // Esta entrada NO es una novedad de producto: es el aviso dentro de la app
  // que la sección 12 de la Política de Privacidad promete para un cambio
  // material. Si se saca de acá, esa promesa queda sin cumplir y hay que
  // sacarla también del texto legal. Van juntas.
  {
    orden: 6,
    id: 'preguntas_guardadas',
    tipo: 'aviso',
    titulo: 'Lo que me preguntas ayuda a mejorar MiPAE',
    detalle: 'Ahora guardamos tus preguntas para saber qué te hace falta y escribir mejor contenido. Solo la pregunta, nunca mi respuesta ni el resto de tu conversación, y si trae datos de un paciente se descarta sola. Lo apagas en Mi perfil cuando quieras.',
  },
  {
    orden: 7,
    id: 'tema',
    tipo: 'herramienta',
    titulo: 'Elige cómo se ve MiPAE',
    detalle: 'En tu perfil puedes cambiar el ambiente de la app entre varios temas, cada uno con su versión clara y oscura, el tamaño del texto y el tipo de letra. Los semáforos clínicos siguen iguales, por seguridad.',
  },
  // Novedad de producto Y aviso dentro de la app de un cambio material (la
  // Bitácora cambió de foco y se abrió a todo perfil; la Política se actualizó
  // el 2026-09-23). Misma regla que 'preguntas_guardadas': va junto al texto
  // legal, y por eso la última frase sigue nombrando ese cambio.
  {
    orden: 8,
    id: 'bienestar',
    tipo: 'herramienta',
    titulo: 'Pausas guiadas en Bienestar',
    detalle: 'Puedes elegir una respiración guiada, seguir una pausa breve o escribir en tu Bitácora. La Bitácora ahora está abierta a todos los perfiles y la Política de Privacidad lo declara.',
  },
  // Aviso dentro de la app de un cambio material: los límites de uso de IA
  // del plan gratis (Términos §6, actualizados el 2026-09-26). Rigen desde el
  // 15-sep y hasta hoy no se habían avisado. Misma regla que
  // 'preguntas_guardadas' y 'bienestar': va junto al texto legal, y si los
  // límites cambian se cambian los dos. Sin compra ni suscripción: lo lee
  // también la app instalada.
  {
    orden: 9,
    id: 'limites_ia',
    tipo: 'aviso',
    titulo: 'Un límite de uso en el plan gratis',
    detalle: 'Los límites del acceso gratuito se explican en los Términos, sección 6. Revisa el aviso del 25 de octubre para conocer las condiciones de cada experiencia. Tus PAE guardados siguen disponibles.',
  },
  // Aviso dentro de la app de un cambio material: la Política de Privacidad
  // (secciones 3 y 6, actualizadas el 2026-09-27) ahora dice que junto a cada
  // pregunta guardada queda lo que pasó con la respuesta (amarre por `ref`,
  // lib/benitoSenales.js) y que el equipo puede mandar el texto de las
  // preguntas a un proveedor de IA para agruparlas ("Qué necesitan" del panel
  // admin, lib/necesidadesIA.js). Misma regla que 'preguntas_guardadas': va
  // junto al texto legal, y si se saca de acá, esa promesa queda sin cumplir.
  // Sin gating: 'preguntas_guardadas' tampoco lo tiene (quien apagó el
  // guardado lo lee igual, y le dice dónde se apaga).
  {
    orden: 10,
    id: 'preguntas_senales',
    tipo: 'aviso',
    titulo: 'Junto a tu pregunta, qué pasó con mi respuesta',
    detalle: 'Junto a cada pregunta que guardamos puede quedar anotado qué pasó con mi respuesta, sin guardar su texto: si te sirvió, si la copiaste, si tuve que buscar en internet y qué herramientas de MiPAE usé. Además, el equipo puede pedirle a un proveedor de inteligencia artificial que agrupe las preguntas por tema: viaja solo su texto, sin tu nombre ni tu correo. Lo apagas en Mi perfil cuando quieras.',
  },
  // Modelos de enfermería en el PAE y en la Enciclopedia (2026-10-02,
  // lib/paeModelos.js y lib/modelosEnfermeria.js). Herramienta, no aviso.
  {
    orden: 11,
    id: 'modelos_enfermeria',
    tipo: 'herramienta',
    titulo: 'Elige tu modelo de enfermería en el PAE',
    detalle: 'En el PAE Rápido y en el Detallado ahora eliges con qué modelo valoras: Henderson, Gordon, Roper, Orem o Astorquiza, el que pide tu ramo. Y en la Enciclopedia están los doce modelos, cada uno con su idea central, cómo se valora, un mismo caso para compararlos, cómo justificarlo en tu PAE y un repaso corto. Si me preguntas por un modelo, te respondo desde esas fichas.',
  },
  // Aviso dentro de la app de un cambio material (Política de Privacidad,
  // sección 2, actualizada el 2026-10-03): Benito usa el perfil para
  // responder y recuerda los ramos que le cuentan. Misma regla que
  // 'preguntas_guardadas': va junto al texto legal, y si se saca de acá la
  // promesa de la sección 12 queda sin cumplir. Hasta el 3-oct este aviso
  // anunciaba también los recuerdos de conversaciones; se retiraron porque la
  // función no está activada (RECUERDOS_ACTIVADOS en lib/benitoRecuerdos.js) y
  // vuelven con un `orden` nuevo el día que se active. El texto anterior nunca
  // llegó a producción, así que nadie reconoció este `orden` con él.
  // Sin compra ni suscripción en el texto: lo lee también la app instalada.
  {
    orden: 12,
    id: 'benito_perfil',
    tipo: 'aviso',
    titulo: 'Uso tu perfil para responderte mejor',
    detalle: 'Tomo en cuenta lo que dice tu perfil, como tu universidad, tu año o tu servicio. Y si me cuentas un ramo que estás cursando, lo guardo junto a tus notas en Memoria de Benito, en Mi perfil, donde lo ves y lo borras. La Política de Privacidad lo detalla en su sección 2.',
  },
  {
    orden: 13,
    id: 'acceso_25_octubre',
    tipo: 'aviso',
    titulo: 'Tu acceso a MiPAE desde el 25 de octubre',
    detalle: 'El 25 de octubre de 2026, a las 00:00, hora de Chile, comienzan las nuevas condiciones. MiPAE Estudiante gratis incluye 1 PAE al mes, compartido entre sus tres formatos, y 3 consultas diarias a Benito. MiPAE Enfermero gratis incluye una evolución completa con IA por semana y por cuenta, sin acumulación, que puedes revisar, editar a mano y copiar. Los errores técnicos y las llamadas internas no descuentan usos adicionales. En ambas experiencias, la Enciclopedia gratuita incluye 5 medicamentos, 3 escalas, valores numéricos de laboratorio y hasta 3 procedimientos propios. MiPAE TENS sigue gratuito en esta etapa, con la Enciclopedia completa. Las membresías y accesos regalados vigentes conservan sus condiciones. No se genera ningún cobro por seguir usando la cuenta gratuita. El detalle está en los Términos, sección 6.',
  },
  {
    orden: 14,
    id: 'aclaracion_prueba_gratuita',
    tipo: 'aviso',
    titulo: 'Aclaración sobre la prueba gratuita',
    detalle: isNative ? 'Las pruebas ya iniciadas mantienen su vigencia y sus condiciones. Puedes revisar tu acceso en Mi perfil.' : 'Las nuevas pruebas de Estudiante Plus (3 días) y Enfermero Plus (7 días) requieren tarjeta. Al terminar se cobra automáticamente la mensualidad si no cancelas antes desde Mi perfil. Las pruebas anteriores mantienen sus condiciones.',
  },
  // La invitación de la campaña del 25 (ajuste de enfoque de Sebastián,
  // 20-oct): cuando está pendiente, la card del inicio toma la forma de
  // membresias/AvisoLoNuevo.jsx, con la utilidad de la experiencia de la
  // persona, una muestra real y una acción clara. Este texto es el que se lee
  // en Novedades del menú. Sin precio ni compra: lo lee la app instalada.
  {
    orden: 15,
    id: 'lo_nuevo_25',
    tipo: 'herramienta',
    titulo: 'Lo nuevo de MiPAE en tu experiencia',
    detalle: 'Te mostramos con un ejemplo lo que ya puedes hacer gratis en tu experiencia, lo que suma cada membresía y cómo probarla. Abrir el aviso no activa nada. La prueba se inicia solo con su botón.',
  },
  // La hoja vuelve UNA vez el 25, ahora con el botón de la prueba (decisión de
  // Sebastián del 20-oct). Por eso esta entrada no existe antes del 25 ni
  // mientras siga el acceso abierto (`desde` y `conAccesoCerrado`, ver
  // novedadVigente): aparece cuando la prueba de verdad suma algo, el mismo
  // día que él cierra el acceso abierto. A quien no tiene prueba que ofrecer
  // (TENS, con membresía, con la prueba usada) QueCambioCard se la reconoce
  // sin mostrar nada.
  {
    orden: 16,
    id: 'lo_nuevo_25_prueba',
    tipo: 'herramienta',
    desde: ESTUDIANTE_FIN_TRANSICION,
    conAccesoCerrado: true,
    titulo: 'Desde hoy puedes probar la membresía de tu experiencia',
    detalle: isNative ? 'Revisa tu acceso y lo nuevo de tu experiencia en Mi perfil.' : 'Estudiante Plus se prueba 3 días y Enfermero Plus 7 días, una vez por cuenta, registrando tarjeta. Al terminar se cobra automáticamente la mensualidad si no cancelas antes desde Mi perfil.',
  },
];

// Una entrada con fecha o condición existe solo desde que se cumple: antes no
// cuenta para "visto", no aparece en Novedades del menú y no se muestra.
export function novedadVigente(n, ahora = Date.now()) {
  if (n.desde && ahora < Date.parse(n.desde)) return false;
  if (n.conAccesoCerrado && accesoPlusAbierto(ahora)) return false;
  return true;
}

export function novedadesVigentes(ahora = Date.now()) {
  return NOVEDADES.filter((n) => novedadVigente(n, ahora));
}

// Identidad del conjunto vigente: el orden más alto del catálogo. Es lo que
// se guarda como "visto" (local y en la cuenta). Estable entre compilaciones.
export function versionCatalogo(ahora = Date.now()) {
  return Math.max(...novedadesVigentes(ahora).map((n) => n.orden));
}

const clave = (userId) => `mipae:que-cambio:visto:${userId || 'anon'}`;

// Orden más alto ya visto EN ESTE DISPOSITIVO. Sin storage devuelve Infinity:
// ante la duda, no molestar (nunca re-mostrar en loop a quien no puede
// persistir el visto).
export function ordenVisto(userId) {
  try {
    const v = localStorage.getItem(clave(userId));
    return v == null ? 0 : (Number(v) || 0);
  } catch {
    return Infinity;
  }
}

// Escribe el espejo local. Nunca baja el valor: un reconocimiento no se
// "des-reconoce" (idempotente ante montajes repetidos o respuestas tardías).
export function guardarOrdenVisto(userId, orden) {
  const n = Number(orden);
  if (!Number.isFinite(n) || n <= 0) return;
  try {
    const actual = ordenVisto(userId);
    if (actual === Infinity || n <= actual) return;
    localStorage.setItem(clave(userId), String(n));
  } catch { /* noop: sin storage, ordenVisto ya devuelve Infinity */ }
}

export function novedadesPendientes(userId, ahora = Date.now()) {
  const visto = ordenVisto(userId);
  return novedadesVigentes(ahora).filter((n) => n.orden > visto).sort((a, b) => a.orden - b.orden);
}

// Marca el conjunto vigente como visto en el ESPEJO local. La versión que
// además lo sube a la cuenta vive en lib/novedadesCuenta.js (misma firma);
// esta se conserva para los consumidores puros y los tests.
export function marcarNovedadesVistas(userId) {
  try {
    localStorage.setItem(clave(userId), String(versionCatalogo()));
  } catch { /* noop */ }
}

// ── Presentación ──────────────────────────────────────────────────────────────
// Etiquetas de grupo del detalle. Los avisos van con su propio título para que
// la información material (datos, condiciones, límites) no se pierda entre
// las mejoras al resumir (auditoría 2026-09-28).
export const GRUPOS_NOVEDAD = Object.freeze({
  herramienta: 'Nuevas herramientas',
  mejora: 'Mejoras',
  aviso: 'Avisos sobre tus datos y condiciones',
});

// Agrupa por tipo, en el orden de GRUPOS_NOVEDAD y respetando el orden del
// catálogo dentro de cada grupo. Un tipo fuera de catálogo cae en 'mejora'.
export function agruparNovedades(lista = NOVEDADES) {
  const porTipo = { herramienta: [], mejora: [], aviso: [] };
  [...lista].sort((a, b) => a.orden - b.orden).forEach((n) => {
    const t = TIPOS_NOVEDAD.includes(n.tipo) ? n.tipo : 'mejora';
    porTipo[t].push(n);
  });
  return TIPOS_NOVEDAD
    .filter((t) => porTipo[t].length > 0)
    .map((t) => ({ tipo: t, titulo: GRUPOS_NOVEDAD[t], items: porTipo[t] }));
}

// Cuerpo del aviso compacto: depende de lo que el conjunto pendiente trae de
// verdad (herramientas nuevas o solo mejoras y avisos), no de un texto fijo.
export function cuerpoAviso(pendientes = []) {
  if (pendientes.some((n) => n.id === 'lo_nuevo_25_prueba')) {
    return isNative ? 'Conoce lo nuevo de tu experiencia.' : 'Prueba la membresía de tu experiencia con tarjeta y cobro automático al terminar si no cancelas antes.';
  }
  if (pendientes.some((n) => n.id === 'lo_nuevo_25')) {
    return 'Conoce lo que ya puedes hacer en tu experiencia y lo que suma cada membresía.';
  }
  if (pendientes.some((n) => n.id === 'aclaracion_prueba_gratuita')) {
    return 'Prueba gratuita: 3 días para Estudiante Plus y 7 para Enfermero Plus.';
  }
  if (pendientes.some((n) => n.id === 'acceso_25_octubre')) {
    return 'Conoce qué seguirá disponible gratis en tu experiencia desde el 25 de octubre.';
  }
  return pendientes.some((n) => n.tipo === 'herramienta')
    ? 'Revisa los cambios y las nuevas herramientas disponibles.'
    : 'Revisa las últimas mejoras de MiPAE.';
}

// Regla de no-interrupción (hueco 18 del análisis del 30-jul): jamás a mitad
// de turno. Con actividad de registro en la última hora (cama viva, no
// práctica), esta apertura no muestra nada; la card reaparece en la próxima
// apertura sin actividad reciente. Pura y testeable.
export const MS_ACTIVIDAD_RECIENTE = 60 * 60 * 1000;

// "¿Está a mitad de turno?": cama viva (no cerrada, no de práctica) tocada en
// la última hora. Exportada para que TODA card del Home que pueda esperar
// (novedades, micro encuestas) use la MISMA regla, no una copia.
export function hayActividadDeTurno(registros = [], ahora = Date.now()) {
  return (registros || []).some(
    (r) => r && !r.cerrado && !r.practica && ahora - (r.updated_at || 0) < MS_ACTIVIDAD_RECIENTE,
  );
}

export function debeMostrarQueCambio({ pendientes, registros = [], ahora = Date.now() } = {}) {
  if (!pendientes || pendientes.length === 0) return false;
  return !hayActividadDeTurno(registros, ahora);
}

